/**
 * Admin Onboarding Page
 *
 * The number the onboarding rework is judged on: how many people get
 * from sign-up to a first published post, and where they leave.
 * - The funnel over the chosen weeks, each step with its share of the step
 *   before it
 * - How long people take between steps (median)
 * - How many skipped each screen, so a drop-off can be told from a skip
 * - The same counts week by week
 */

import { useMemo } from "react";
import {
  AdminListToolbar,
  type ToolbarSortOption,
} from "@/components/lists/AdminListToolbar";
import {
  type ListUrlConfig,
  sortRows,
  useListUrlState,
} from "@/components/lists/useListUrlState";
import { Spinner } from "@/components/ui/Spinner";
import { useAdminOnboardingFunnel } from "@/hooks/useAdmin";
import {
  FUNNEL_STAGES,
  type FunnelStage,
  type OnboardingFunnel,
} from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { formatDuration, stepShare } from "@/utils/onboardingFunnel";

const WINDOWS = [4, 8, 12, 26] as const;
const DEFAULT_WINDOW = 8;

const SORT_OPTIONS: readonly ToolbarSortOption[] = [
  { field: "weekStart", label: "Week" },
];

/** The by-week table lists the newest week first unless the address says otherwise. */
const URL_CONFIG: ListUrlConfig = {
  filterKeys: ["weeks", "stage"],
  sortFields: SORT_OPTIONS.map((o) => o.field),
  defaultSort: { field: "weekStart", dir: "desc" },
};

const STAGE_LABELS: Record<FunnelStage, string> = {
  signups: "Signed up",
  verified: "Verified",
  questionsDone: "Answered the questions",
  voiceDone: "Picked a voice",
  firstDraft: "Generated a first draft",
  firstPost: "Published a first post",
};

/** The steps whose duration is reported, in funnel order. */
const TRANSITION_LABELS: Record<string, string> = {
  "verified>questionsDone": "Verified to questions done",
  "questionsDone>voiceDone": "Questions to voice",
  "voiceDone>firstDraft": "Voice to first draft",
  "firstDraft>firstPost": "First draft to first post",
  "verified>firstPost": "Verified to first post",
};

/** `YYYY-MM-DD` as a short date in UTC. */
function formatDay(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? day
    : new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }).format(parsed);
}

function FunnelBars({ funnel }: { funnel: OnboardingFunnel }) {
  const max = Math.max(1, ...FUNNEL_STAGES.map((s) => funnel.totals[s]));
  return (
    <ol className="space-y-3">
      {FUNNEL_STAGES.map((stage) => {
        const count = funnel.totals[stage];
        const share = stepShare(funnel.totals, stage);
        return (
          <li key={stage} className="space-y-1">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="font-medium">{STAGE_LABELS[stage]}</span>
              <span className="tabular-nums text-muted-foreground">
                <span className="font-semibold text-foreground">{count}</span>
                {share !== null && (
                  <span className="ml-2">{share}% of previous</span>
                )}
              </span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${(count / max) * 100}%` }}
                role="presentation"
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function AdminOnboardingPage() {
  const list = useListUrlState(URL_CONFIG);
  const requested = Number(list.filters.weeks);
  const weeks = WINDOWS.find((w) => w === requested) ?? DEFAULT_WINDOW;
  const stage = FUNNEL_STAGES.find((s) => s === list.filters.stage);
  const { data, isLoading, error } = useAdminOnboardingFunnel(weeks);

  // The whole window arrives at once, so sorting and narrowing the by-week
  // table here loses nothing.
  const weekRows = useMemo(
    () =>
      sortRows(data?.weeks ?? [], list.sort, {
        weekStart: (week) => week.weekStart,
      }),
    [data?.weeks, list.sort]
  );
  const shownStages = stage ? [stage] : FUNNEL_STAGES;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Onboarding</h1>
        <p className="text-sm text-muted-foreground">
          From sign-up to a first published post, and where people leave.
        </p>
      </div>

      <AdminListToolbar
        label="Onboarding"
        filters={[
          {
            id: "weeks",
            label: "Window",
            kind: "select",
            options: WINDOWS.map((w) => ({
              value: w === DEFAULT_WINDOW ? "" : String(w),
              label: `${w} weeks`,
            })),
            value: weeks === DEFAULT_WINDOW ? "" : String(weeks),
            onChange: (v) => list.setFilter("weeks", v),
          },
          {
            id: "stage",
            label: "Stage in the by-week table",
            kind: "select",
            options: [
              { value: "", label: "All stages" },
              ...FUNNEL_STAGES.map((st) => ({
                value: st,
                label: STAGE_LABELS[st],
              })),
            ],
            value: stage ?? "",
            onChange: (v) => list.setFilter("stage", v),
          },
        ]}
        sort={{
          options: SORT_OPTIONS,
          value: list.sort,
          onChange: list.setSort,
          scope: "server",
        }}
        hasActive={list.hasActive}
        onClear={list.clear}
      />

      {isLoading ? (
        <div className="flex h-60 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error || !data ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center text-sm text-destructive">
          {getErrorMessage(error, "Failed to load the onboarding funnel.")}
        </div>
      ) : (
        <>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <section
              aria-label="Funnel"
              className="space-y-4 rounded-xl border border-border/50 bg-card p-5"
            >
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Funnel, {formatDay(data.range.from)} to{" "}
                {formatDay(data.range.to)}
              </h2>
              <FunnelBars funnel={data} />
            </section>

            <div className="space-y-6">
              <section
                aria-label="Time between steps"
                className="space-y-3 rounded-xl border border-border/50 bg-card p-5"
              >
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Median time between steps
                </h2>
                <dl className="space-y-2 text-sm">
                  {Object.entries(TRANSITION_LABELS).map(([key, label]) => (
                    <div key={key} className="flex justify-between gap-3">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="font-medium tabular-nums">
                        {formatDuration(data.medianMs[key])}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section
                aria-label="Skipped screens"
                className="space-y-3 rounded-xl border border-border/50 bg-card p-5"
              >
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Skipped
                </h2>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">The questions</dt>
                    <dd className="font-medium tabular-nums">
                      {data.skipped.questions}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">The voice screen</dt>
                    <dd className="font-medium tabular-nums">
                      {data.skipped.voice}
                    </dd>
                  </div>
                </dl>
              </section>
            </div>
          </div>

          <section
            aria-label="By week"
            className="overflow-x-auto rounded-xl border border-border/50 bg-card"
          >
            <table className="w-full text-sm">
              <caption className="p-5 pb-3 text-left text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                By week
              </caption>
              <thead>
                <tr className="border-b border-border/50 text-left text-xs text-muted-foreground">
                  <th scope="col" className="px-5 py-2 font-medium">
                    Week of
                  </th>
                  {shownStages.map((stage) => (
                    <th
                      key={stage}
                      scope="col"
                      className="px-3 py-2 text-right font-medium"
                    >
                      {STAGE_LABELS[stage]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weekRows.map((week) => (
                  <tr
                    key={week.weekStart}
                    className="border-b border-border/30 last:border-0"
                  >
                    <th scope="row" className="px-5 py-2 text-left font-medium">
                      {formatDay(week.weekStart)}
                    </th>
                    {shownStages.map((stage) => (
                      <td
                        key={stage}
                        className="px-3 py-2 text-right tabular-nums"
                      >
                        {week.counts[stage]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <p className="text-xs text-muted-foreground">
            Each person counts once per step, in the week of their first event.
            Weeks start on Monday (UTC). Sign-ups are email registrations; a
            Google sign-up has no registration row, so Verified can be higher
            than Signed up in a busy week. Events from before this report
            existed are not counted.
          </p>
        </>
      )}
    </div>
  );
}
